import { Box, Button, Card, CardBody, Checkbox, CheckboxGroup, Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel, Icon, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Radio, RadioGroup, Skeleton, Stack, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr } from "@chakra-ui/react";
import { Tooltip } from "../../../common/components/Tooltip";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight, FiShoppingCart } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  type AdmittedSupplierDto,
  ProcedureType,
  QualificationSchemeStartBlock,
  type SourcingEventDto,
  useCreateQualificationSchemeCompetitionMutation,
  useGetQualificationSchemeCompetitionsQuery,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { urls } from "../../../urls";
import { SourcingEventStatusTag } from "../SourcingEventStatusTag";

const PROCEDURES = [ProcedureType.Restricted, ProcedureType.NegotiatedWithPriorCall];

export const QualificationSchemeCompetitionsTab = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const displayDateWithTime = useDisplayDateWithTime();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetQualificationSchemeCompetitionsQuery({ eventId: event.id });
  const [isStarting, setIsStarting] = useState(false);

  if (isLoading) {
    return (
      <Stack spacing="4" aria-busy>
        <Skeleton height="10" rounded="md" />
        <Skeleton height="32" rounded="xl" />
      </Stack>
    );
  }
  if (isError || !data) {
    return (
      <Flex direction="column" align="center" py="10" gap="3">
        <Text color="smError">{t("Could not load the competitions.")}</Text>
        <Button variant="outline" onClick={() => refetch()}>
          {t("Try again")}
        </Button>
      </Flex>
    );
  }

  const rows = data.rows ?? [];
  const scheme = event.eventContext.type === "Public" ? event.eventContext.qualificationScheme : null;
  const blockedReason = (() => {
    switch (data.startBlock) {
      case QualificationSchemeStartBlock.NotYetOpen:
        return scheme ?
            t("The system runs from {{date}} — competitions can start then.", { date: displayDate(scheme.validFrom) })
          : null;
      case QualificationSchemeStartBlock.Closed:
        return scheme ? t("The system closed on {{date}}.", { date: displayDate(scheme.validTo) }) : null;
      case QualificationSchemeStartBlock.Ended:
        return data.endedAt ? t("The system closed on {{date}}.", { date: displayDate(data.endedAt) }) : null;
      case QualificationSchemeStartBlock.NotPublished:
        return t("Competitions can be started once the system is announced, and not after it has been cancelled.");
      case QualificationSchemeStartBlock.NoAdmittedSuppliers:
        return t("At least one supplier must be admitted before a competition can start (forsyningsforskriften § 13-3).");
      default:
        return null;
    }
  })();

  return (
    <Stack spacing="5">
      <Flex align="center" justify="space-between" gap="4" wrap="wrap">
        <Text fontSize="sm" color="smMuted">
          {data.canStartCompetition ?
            t("The system's notice is the call for competition, so a competition needs no notice of its own.")
          : blockedReason}
        </Text>
        <Tooltip label={data.canStartCompetition ? undefined : blockedReason} shouldWrapChildren>
          <Button colorScheme="teal" isDisabled={!data.canStartCompetition} onClick={() => setIsStarting(true)}>
            {t("New competition")}
          </Button>
        </Tooltip>
      </Flex>

      {rows.length === 0 ?
        <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
          <CardBody py="10">
            <Flex direction="column" align="center" gap="3" textAlign="center">
              <Icon as={FiShoppingCart} boxSize="8" color="smPrimary" />
              <Text fontWeight="semibold" color="smText">
                {t("No competitions have been run yet")}
              </Text>
              <Text fontSize="sm" color="smMuted" maxW="md">
                {data.canStartCompetition ? t("Start one whenever you need to buy — no new notice is needed.") : blockedReason}
              </Text>
            </Flex>
          </CardBody>
        </Card>
      : <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
          <Table size="md">
            <Thead>
              <Tr>
                <Th>{t("Competition")}</Th>
                <Th>{t("Procedure")}</Th>
                <Th>{t("Status")}</Th>
                <Th>{t("Bid deadline")}</Th>
                <Th>{t("Invited")}</Th>
                <Th aria-hidden />
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr
                  key={row.eventId}
                  cursor="pointer"
                  _hover={{ bg: "smBackgroundSecondary" }}
                  onClick={() => navigate(urls.app.sourcingEvents.view.go(row.eventId))}
                >
                  <Td>
                    <Text fontWeight="semibold" fontSize="sm" color="smText" noOfLines={1}>
                      {row.title}
                    </Text>
                  </Td>
                  <Td fontSize="sm" color="smMuted">
                    {t(row.procedureType)}
                  </Td>
                  <Td>
                    <SourcingEventStatusTag status={row.status} />
                  </Td>
                  <Td fontSize="sm" color="smMuted" whiteSpace="nowrap">
                    {row.bidSubmissionDeadline ? displayDateWithTime(row.bidSubmissionDeadline) : "–"}
                  </Td>
                  <Td fontSize="sm" color="smMuted" fontFamily="mono">
                    {row.invitedCount}
                  </Td>
                  <Td textAlign="right">
                    <Icon as={FiChevronRight} color="smMuted" />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      }

      {isStarting && (
        <StartCompetitionModal
          schemeId={event.id}
          admitted={data.admittedSuppliers ?? []}
          onClose={() => setIsStarting(false)}
        />
      )}
    </Stack>
  );
};

const StartCompetitionModal = ({
  schemeId,
  admitted,
  onClose,
}: {
  schemeId: string;
  admitted: AdmittedSupplierDto[];
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const [title, setTitle] = useState("");
  const [procedure, setProcedure] = useState<ProcedureType>(ProcedureType.Restricted);
  const [invited, setInvited] = useState<string[]>(admitted.map((supplier) => supplier.organizationId));
  const [reason, setReason] = useState("");
  const [create, { isLoading }] = useCreateQualificationSchemeCompetitionMutation();
  const noneInvited = invited.length === 0;
  const narrowed = !noneInvited && invited.length < admitted.length;
  const reasonMissing = narrowed && reason.trim().length === 0;

  const submit = async () => {
    try {
      const created = await create({
        eventId: schemeId,
        createQualificationSchemeCompetitionRequestDto: {
          title,
          procedureType: procedure,
          invitedOrganizationIds: narrowed ? invited : undefined,
          narrowingReason: narrowed ? reason : undefined,
        },
      }).unwrap();
      navigate(urls.app.sourcingEvents.edit.go(created.id).info);
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Modal isOpen onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("New competition")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="5">
            <FormControl isRequired>
              <FormLabel>{t("What are you buying?")}</FormLabel>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            </FormControl>
            <FormControl as="fieldset">
              <FormLabel as="legend">{t("Procedure")}</FormLabel>
              <RadioGroup value={procedure} onChange={(value) => setProcedure(value as ProcedureType)}>
                <Stack>
                  {PROCEDURES.map((option) => (
                    <Radio key={option} value={option}>
                      {t(option)}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
            <FormControl as="fieldset" isInvalid={noneInvited}>
              <FormLabel as="legend">{t("Invited suppliers")}</FormLabel>
              <CheckboxGroup value={invited} onChange={(values) => setInvited(values as string[])}>
                <Stack>
                  {admitted.map((supplier) => (
                    <Checkbox key={supplier.organizationId} value={supplier.organizationId}>
                      {supplier.name}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
              <FormHelperText>
                {t("Every admitted supplier is invited unless the system's selection rules let you invite fewer.")}
              </FormHelperText>
              <FormErrorMessage>{t("Choose at least one supplier to invite.")}</FormErrorMessage>
            </FormControl>
            {narrowed && (
              <FormControl isRequired isInvalid={reasonMissing}>
                <FormLabel>{t("Why are not all admitted suppliers invited?")}</FormLabel>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
                <FormHelperText>
                  {t(
                    "Name the selection rule from the system that you apply. The reason goes to the protocol (forsyningsforskriften § 21-5).",
                  )}
                </FormHelperText>
                <FormErrorMessage>{t("Give a reason for inviting fewer suppliers.")}</FormErrorMessage>
              </FormControl>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap="2" justifyContent="flex-start" flexDirection="row-reverse">
          <Button
            colorScheme="teal"
            isDisabled={title.trim().length === 0 || noneInvited || reasonMissing}
            isLoading={isLoading}
            onClick={submit}
          >
            {t("Create")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
